import React from 'react';
import styled from 'styled-components';

const DocsPage = styled.div`
  max-width: 800px;
  margin: 0 auto;
  
  h1 {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 2.5rem;
    font-weight: 700;
    color: #ffffff;
    margin-bottom: 1rem;
    line-height: 1.2;
  }
  
  h2 {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.8rem;
    font-weight: 600;
    color: #ffffff;
    margin: 2rem 0 1rem 0;
    line-height: 1.3;
  }
  
  h3 {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.4rem;
    font-weight: 600;
    color: #ffffff;
    margin: 1.5rem 0 0.75rem 0;
    line-height: 1.4;
  }
  
  p {
    font-family: 'Roboto', sans-serif;
    font-size: 1rem;
    line-height: 1.6;
    color: #e5e7eb;
    margin-bottom: 1rem;
  }
  
  ul, ol {
    margin: 1rem 0;
    padding-left: 1.5rem;
  }
  
  li {
    font-family: 'Roboto', sans-serif;
    font-size: 1rem;
    line-height: 1.6;
    color: #e5e7eb;
    margin-bottom: 0.5rem;
  }
  
  .network-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin: 2rem 0;
  }
  
  .network-card {
    background: rgba(168, 85, 247, 0.1);
    border-radius: 12px;
    padding: 1.5rem;
    text-align: center;
  }
  
  .network-name {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.1rem;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 0.5rem;
  }
  
  .network-chainid {
    font-family: 'monospace', sans-serif;
    font-size: 0.9rem;
    color: #a855f7;
    margin-bottom: 0.5rem;
  }
  
  .step {
    background: rgba(168, 85, 247, 0.1);
    border-radius: 12px;
    padding: 1.5rem;
    margin: 1.5rem 0;
  }
  
  .step-number {
    background: #a855f7;
    color: #ffffff;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.875rem;
    margin-bottom: 1rem;
  }
  
  .step-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.2rem;
    font-weight: 600;
    color: #ffffff;
    margin-bottom: 0.75rem;
  }
  
  .step-content {
    font-family: 'Inter', sans-serif;
    color: #e5e7eb;
    line-height: 1.6;
  }
  
  .info-box {
    background: rgba(59, 130, 246, 0.1);
    border-radius: 8px;
    padding: 1rem;
    margin: 1rem 0;
  }
  
  .info-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    color: #3b82f6;
    margin-bottom: 0.5rem;
  }
  
  .info-text {
    font-family: 'Inter', sans-serif;
    color: #d1d5db;
    font-size: 0.95rem;
  }
  
  .warning-box {
    background: rgba(245, 158, 11, 0.1);
    border-radius: 8px;
    padding: 1rem;
    margin: 1rem 0;
  }
  
  .warning-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-weight: 600;
    color: #f59e0b;
    margin-bottom: 0.5rem;
  }
  
  .warning-text {
    font-family: 'Inter', sans-serif;
    color: #d1d5db;
    font-size: 0.95rem;
  }
  
  .feature-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
    margin: 2rem 0;
  }
  
  .feature-card {
    background: rgba(168, 85, 247, 0.1);
    border-radius: 12px;
    padding: 1.5rem;
  }
  
  .feature-title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 1.2rem;
    font-weight: 600;
    color: #a855f7;
    margin-bottom: 0.75rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
  
  @media (max-width: 768px) {
    h1 {
      font-size: 2rem;
    }
    
    h2 {
      font-size: 1.5rem;
    }
    
    .network-grid {
      grid-template-columns: repeat(2, 1fr);
    }
    
    .feature-grid {
      grid-template-columns: 1fr;
    }
  }
`;

const BridgeOperations: React.FC = () => {
  return (
    <DocsPage>
      <h1>Bridge Operations</h1>
      
      <p>
        The Mavrk Bridge system enables seamless cross-chain asset transfers between major blockchain networks and Ink L2. Built on the Relay Protocol, it provides secure, efficient, and cost-effective bridging for ETH and other supported assets.
      </p>

      <h2>Supported Networks</h2>

      <div className="network-grid">
        <div className="network-card">
          <div className="network-name">Ethereum Mainnet</div>
          <div className="network-chainid">Chain ID: 1</div>
        </div>
        <div className="network-card">
          <div className="network-name">Base Mainnet</div>
          <div className="network-chainid">Chain ID: 8453</div>
        </div>
        <div className="network-card">
          <div className="network-name">Polygon</div>
          <div className="network-chainid">Chain ID: 137</div>
        </div>
        <div className="network-card">
          <div className="network-name">Optimism</div>
          <div className="network-chainid">Chain ID: 10</div>
        </div>
        <div className="network-card">
          <div className="network-name">Arbitrum One</div>
          <div className="network-chainid">Chain ID: 42161</div>
        </div>
        <div className="network-card">
          <div className="network-name">Avalanche C-Chain</div>
          <div className="network-chainid">Chain ID: 43114</div>
        </div>
        <div className="network-card">
          <div className="network-name">Binance Smart Chain</div>
          <div className="network-chainid">Chain ID: 56</div>
        </div>
        <div className="network-card">
          <div className="network-name">Ink L2 (Destination)</div>
          <div className="network-chainid">Chain ID: 57073</div>
        </div>
      </div>

      <h2>How to Use the Bridge</h2>

      <div className="step">
        <div className="step-number">1</div>
        <div>
          <h3 className="step-title">Access the Bridge</h3>
          <div className="step-content">
            <ol>
              <li>Navigate to the <strong>"Bridge & Swap"</strong> section</li>
              <li>Click the <strong>"Bridge"</strong> tab (default view)</li>
              <li>The bridge interface will load with network selection</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">2</div>
        <div>
          <h3 className="step-title">Select Source Chain</h3>
          <div className="step-content">
            <ol>
              <li><strong>Click the dropdown</strong> next to "From"</li>
              <li><strong>Choose your source chain</strong> (e.g., Ethereum, Base, Polygon)</li>
              <li><strong>Wallet will automatically switch</strong> to the selected network</li>
              <li><strong>Balance will update</strong> to show your ETH balance on that chain</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">3</div>
        <div>
          <h3 className="step-title">Set Bridge Amount</h3>
          <div className="step-content">
            <h4>Percentage Buttons</h4>
            <ul>
              <li><strong>25%:</strong> Bridge 25% of your balance</li>
              <li><strong>50%:</strong> Bridge 50% of your balance</li>
              <li><strong>75%:</strong> Bridge 75% of your balance</li>
              <li><strong>Max:</strong> Bridge 95% of your balance (5% reserved for gas)</li>
            </ul>

            <h4>Manual Input</h4>
            <ul>
              <li><strong>Enter amount</strong> directly in the input field</li>
              <li><strong>Real-time validation</strong> ensures you don't exceed balance</li>
              <li><strong>Gas estimation</strong> shows estimated fees</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">4</div>
        <div>
          <h3 className="step-title">Review and Bridge</h3>
          <div className="step-content">
            <ol>
              <li><strong>Review details:</strong>
                <ul>
                  <li>Source chain and amount</li>
                  <li>Destination chain (Ink L2)</li>
                  <li>Estimated fees and time</li>
                  <li>Exchange rate</li>
                </ul>
              </li>
              <li><strong>Click "Bridge"</strong></li>
              <li><strong>Confirm transaction</strong> in your wallet</li>
              <li><strong>Wait for confirmation</strong> (typically 1-3 minutes)</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">5</div>
        <div>
          <h3 className="step-title">Monitor Progress</h3>
          <div className="step-content">
            <ol>
              <li><strong>Transaction Hash:</strong> Click to view on source chain explorer</li>
              <li><strong>Request ID:</strong> Click to monitor on Relay Protocol</li>
              <li><strong>Status Updates:</strong> Real-time progress tracking</li>
              <li><strong>Completion:</strong> Notification when bridge completes</li>
            </ol>
          </div>
        </div>
      </div>

      <h2>Bridge Process Details</h2>

      <div className="feature-grid">
        <div className="feature-card">
          <h3 className="feature-title">Step 1: Transaction Initiation</h3>
          <ul>
            <li>User initiates bridge transaction on source chain</li>
            <li>Relay Protocol processes the request</li>
            <li>Request ID generated for tracking</li>
            <li>Transaction confirmed on source chain</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">Step 2: Cross-Chain Processing</h3>
          <ul>
            <li>Relay validators verify the transaction</li>
            <li>Cross-chain message sent to destination</li>
            <li>Ink L2 receives the bridge request</li>
            <li>Validation occurs on destination chain</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">Step 3: Asset Delivery</h3>
          <ul>
            <li>ETH minted on Ink L2 (if bridging ETH)</li>
            <li>Tokens transferred to user's Ink L2 address</li>
            <li>Bridge completion confirmed</li>
            <li>User receives bridged assets</li>
          </ul>
        </div>
      </div>

      <h2>Network Switching</h2>

      <div className="info-box">
        <div className="info-title">Automatic Switching</div>
        <div className="info-text">
          <ul>
            <li><strong>Wallet Integration:</strong> Automatically switches your wallet to the selected chain</li>
            <li><strong>Balance Updates:</strong> Real-time balance fetching for each network</li>
            <li><strong>RPC Optimization:</strong> Uses reliable RPC endpoints for each chain</li>
            <li><strong>Error Handling:</strong> Fallback mechanisms for failed switches</li>
          </ul>
        </div>
      </div>

      <div className="info-box">
        <div className="info-title">Manual Verification</div>
        <div className="info-text">
          <ul>
            <li><strong>Check Network:</strong> Verify your wallet is on the correct chain</li>
            <li><strong>Balance Confirmation:</strong> Ensure you have sufficient balance</li>
            <li><strong>Gas Estimation:</strong> Check gas fees before bridging</li>
            <li><strong>Transaction History:</strong> Review previous bridge transactions</li>
          </ul>
        </div>
      </div>

      <h2>Fee Structure</h2>

      <div className="feature-grid">
        <div className="feature-card">
          <h3 className="feature-title">Bridge Fees</h3>
          <ul>
            <li><strong>Relay Protocol Fees:</strong> Varies by network and amount</li>
            <li><strong>Gas Costs:</strong> Source chain transaction fees</li>
            <li><strong>Exchange Rates:</strong> Real-time rate calculations</li>
            <li><strong>Total Cost:</strong> Transparent fee breakdown</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">Cost Optimization</h3>
          <ul>
            <li><strong>Batch Bridging:</strong> Bridge larger amounts to reduce per-transaction costs</li>
            <li><strong>Network Selection:</strong> Choose networks with lower gas fees</li>
            <li><strong>Timing:</strong> Bridge during low-congestion periods</li>
            <li><strong>Amount Planning:</strong> Use percentage buttons for optimal amounts</li>
          </ul>
        </div>
      </div>

      <h2>Supported Assets</h2>

      <div className="info-box">
        <div className="info-title">Currently Supported</div>
        <div className="info-text">
          <ul>
            <li><strong>ETH:</strong> Native Ethereum on all supported chains</li>
            <li><strong>WETH:</strong> Wrapped Ethereum (automatically handled)</li>
            <li><strong>More assets coming soon</strong></li>
          </ul>
        </div>
      </div>

      <div className="info-box">
        <div className="info-title">Future Support</div>
        <div className="info-text">
          <ul>
            <li><strong>ERC-20 Tokens:</strong> Major tokens on each chain</li>
            <li><strong>Stablecoins:</strong> USDC, USDT, DAI</li>
            <li><strong>Popular Tokens:</strong> Chain-specific popular tokens</li>
          </ul>
        </div>
      </div>

      <h2>Security Features</h2>

      <div className="feature-grid">
        <div className="feature-card">
          <h3 className="feature-title">🔒 Relay Protocol Security</h3>
          <ul>
            <li>Decentralized validators</li>
            <li>Cryptographic proofs</li>
            <li>Audit history</li>
            <li>Battle-tested protocol</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">🛡️ Platform Security</h3>
          <ul>
            <li>Input validation</li>
            <li>Balance verification</li>
            <li>Transaction monitoring</li>
            <li>Error handling</li>
          </ul>
        </div>

        <div className="feature-card">
          <h3 className="feature-title">🔍 Transparency</h3>
          <ul>
            <li>Open source code</li>
            <li>Explorer links</li>
            <li>Real-time status</li>
            <li>Audit reports</li>
          </ul>
        </div>
      </div>

      <h2>Best Practices</h2>

      <div className="step">
        <div className="step-number">6</div>
        <div>
          <h3 className="step-title">Before Bridging</h3>
          <div className="step-content">
            <ol>
              <li><strong>Verify Network:</strong> Ensure you're on the correct source chain</li>
              <li><strong>Check Balance:</strong> Confirm you have sufficient ETH</li>
              <li><strong>Estimate Fees:</strong> Review total costs before bridging</li>
              <li><strong>Plan Amount:</strong> Use percentage buttons for optimal amounts</li>
              <li><strong>Check Destination:</strong> Verify your Ink L2 address</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">7</div>
        <div>
          <h3 className="step-title">During Bridge</h3>
          <div className="step-content">
            <ol>
              <li><strong>Monitor Progress:</strong> Watch the transaction status</li>
              <li><strong>Don't Close Browser:</strong> Keep the page open during bridging</li>
              <li><strong>Check Explorers:</strong> Use provided links to track progress</li>
              <li><strong>Be Patient:</strong> Bridges can take 1-5 minutes depending on network</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="step">
        <div className="step-number">8</div>
        <div>
          <h3 className="step-title">After Bridge</h3>
          <div className="step-content">
            <ol>
              <li><strong>Verify Receipt:</strong> Check your Ink L2 balance</li>
              <li><strong>Save Transaction Hash:</strong> Keep records of successful bridges</li>
              <li><strong>Update Portfolio:</strong> Add bridged assets to your holdings</li>
              <li><strong>Plan Next Steps:</strong> Consider what to do with bridged assets</li>
            </ol>
          </div>
        </div>
      </div>

      <div className="info-box">
        <div className="info-title">Pro Tip</div>
        <div className="info-text">
          Always bridge during low-congestion periods for faster processing and lower fees. The bridge system is designed for efficiency, but network conditions can affect processing times.
        </div>
      </div>
    </DocsPage>
  );
};

export default BridgeOperations;
